import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Search, Users, Calendar, Lightbulb, Briefcase, GraduationCap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';

interface SearchResult {
  id: string;
  title: string;
  subtitle?: string;
  route: string;
}

interface SearchResults {
  members: SearchResult[];
  events: SearchResult[];
  insights: SearchResult[];
  opportunities: SearchResult[];
  workshops: SearchResult[];
}

const GlobalSearch = () => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResults>({ members: [], events: [], insights: [], opportunities: [], workshops: [] });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, []);

  const search = useCallback(async (q: string) => {
    if (q.length < 2) {
      setResults({ members: [], events: [], insights: [], opportunities: [], workshops: [] });
      return;
    }
    setLoading(true);
    const pattern = `%${q}%`;

    const [members, events, insights, opportunities, workshops] = await Promise.all([
      supabase.from('club_members').select('id, name, company').ilike('name', pattern).limit(5),
      supabase.from('events').select('id, title, location').ilike('title', pattern).limit(5),
      supabase.from('tech_insights').select('id, title, category').ilike('title', pattern).limit(5),
      supabase.from('opportunities').select('id, title, company').ilike('title', pattern).eq('status', 'active').limit(5),
      supabase.from('workshops').select('id, title, instructor_name').ilike('title', pattern).limit(5),
    ]);

    setResults({
      members: (members.data || []).map(m => ({ id: m.id, title: m.name, subtitle: m.company, route: `/members` })),
      events: (events.data || []).map(e => ({ id: e.id, title: e.title, subtitle: e.location || '', route: `/events` })),
      insights: (insights.data || []).map(i => ({ id: i.id, title: i.title, subtitle: i.category, route: `/insights/${i.id}` })),
      opportunities: (opportunities.data || []).map(o => ({ id: o.id, title: o.title, subtitle: o.company, route: `/opportunities` })),
      workshops: (workshops.data || []).map(w => ({ id: w.id, title: w.title, subtitle: w.instructor_name, route: `/workshops` })),
    });
    setLoading(false);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => search(query), 300);
    return () => clearTimeout(timer);
  }, [query, search]);

  const handleSelect = (route: string) => {
    setOpen(false);
    setQuery('');
    navigate(route);
  };

  const hasResults = Object.values(results).some(r => r.length > 0);

  const groups = [
    { key: 'members', label: 'Membres', icon: Users, items: results.members },
    { key: 'events', label: 'Événements', icon: Calendar, items: results.events },
    { key: 'insights', label: 'Insights', icon: Lightbulb, items: results.insights },
    { key: 'opportunities', label: 'Opportunités', icon: Briefcase, items: results.opportunities },
    { key: 'workshops', label: 'Workshops', icon: GraduationCap, items: results.workshops },
  ];

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        className="hidden md:flex items-center gap-2 text-muted-foreground border-border/50 hover:border-primary/50"
      >
        <Search className="h-4 w-4" />
        <span className="text-sm">Rechercher...</span>
        <kbd className="ml-2 pointer-events-none inline-flex h-5 items-center gap-1 rounded border border-border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
          ⌘K
        </kbd>
      </Button>

      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput
          placeholder="Rechercher membres, événements, insights..."
          value={query}
          onValueChange={setQuery}
        />
        <CommandList>
          {loading && <div className="py-6 text-center text-sm text-muted-foreground">Recherche...</div>}
          {!loading && query.length >= 2 && !hasResults && (
            <CommandEmpty>Aucun résultat trouvé.</CommandEmpty>
          )}
          {groups.map(group => {
            if (group.items.length === 0) return null;
            const Icon = group.icon;
            return (
              <CommandGroup key={group.key} heading={group.label}>
                {group.items.map(item => (
                  <CommandItem
                    key={item.id}
                    value={`${group.key}-${item.title}`}
                    onSelect={() => handleSelect(item.route)}
                    className="cursor-pointer"
                  >
                    <Icon className="mr-2 h-4 w-4 text-muted-foreground" />
                    <div className="flex flex-col">
                      <span>{item.title}</span>
                      {item.subtitle && <span className="text-xs text-muted-foreground">{item.subtitle}</span>}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            );
          })}
        </CommandList>
      </CommandDialog>
    </>
  );
};

export default GlobalSearch;
